import { startTransition, useEffect, useState } from 'react';
import { type SettingInfoDto } from '../../../shared/api/pipeex/pcfSettingsClient';
import { buildProfileKey } from '../lib/profile';
import { traceMappingFlow, warnMappingFlow } from '../lib/mappingTrace';
import { perf } from '../../../shared/lib/perfLogger';
import {
  loadProfileSettings,
  readCachedProfileInspection,
  type ProfileInspectionCandidate,
} from '../lib/project-gate/profileSettingsInspection';

export interface ProfileInspectionState {
  projectKey: string | null;
  modelVersion: number | null;
  profileSignature: string;
  isLoading: boolean;
  candidates: ProfileInspectionCandidate[];
}

const emptyProfileInspectionState: ProfileInspectionState = {
  projectKey: null,
  modelVersion: null,
  profileSignature: '',
  isLoading: false,
  candidates: [],
};

const PROFILE_MATCH_INSPECTION_STALL_MS = 8_000;

/**
 * Loads and caches the settings DTO of every profile under inspection.
 * Owns the fetch lifecycle (cache short-circuit, stall warning, stale-result
 * drops) so the matches hook only orchestrates catalogs and scoring.
 */
export function useProfileSettingsInspection(input: {
  enabled: boolean;
  projectKey: string | null;
  normalizedProjectKey: string | null;
  modelVersion: number | null;
  profiles: SettingInfoDto[];
  profilesToInspect: SettingInfoDto[];
  profileSignature: string;
  preferredProfileKey?: string | null;
}): ProfileInspectionState {
  const {
    enabled,
    projectKey,
    normalizedProjectKey,
    modelVersion,
    profiles,
    profilesToInspect,
    profileSignature,
    preferredProfileKey,
  } = input;
  const [profileInspection, setProfileInspection] =
    useState<ProfileInspectionState>(emptyProfileInspectionState);

  useEffect(() => {
    if (!enabled || profiles.length === 0 || !profileSignature) {
      traceMappingFlow('project profile match inspection skipped', {
        enabled,
        projectKey,
        normalizedProjectKey,
        modelVersion,
        profileCount: profiles.length,
        profileSignaturePresent: Boolean(profileSignature),
      });
      setProfileInspection(emptyProfileInspectionState);
      return;
    }

    const cachedCandidates = readCachedProfileInspection(profilesToInspect);
    if (cachedCandidates) {
      traceMappingFlow('project profile match inspection fully cached', {
        projectKey,
        normalizedProjectKey,
        modelVersion,
        profileCount: profilesToInspect.length,
        totalProfileCount: profiles.length,
        preferredProfileKey: preferredProfileKey ?? null,
        cachedProfileKeys: cachedCandidates.map((candidate) => buildProfileKey(candidate.profile)),
      });
      startTransition(() => {
        setProfileInspection({
          projectKey: normalizedProjectKey,
          modelVersion,
          profileSignature,
          isLoading: false,
          candidates: cachedCandidates,
        });
      });
      return;
    }

    let isActive = true;
    setProfileInspection({
      projectKey: normalizedProjectKey,
      modelVersion,
      profileSignature,
      isLoading: true,
      candidates: [],
    });

    const inspectProfiles = async () => {
      const stallTimer = globalThis.setTimeout(() => {
        warnMappingFlow('project profile match inspection still pending', {
          projectKey,
          normalizedProjectKey,
          modelVersion,
          profileCount: profilesToInspect.length,
          totalProfileCount: profiles.length,
          preferredProfileKey: preferredProfileKey ?? null,
          profileSignature,
          timeoutMs: PROFILE_MATCH_INSPECTION_STALL_MS,
        });
      }, PROFILE_MATCH_INSPECTION_STALL_MS);
      traceMappingFlow('project profile match inspection start', {
        projectKey,
        normalizedProjectKey,
        modelVersion,
        profileCount: profilesToInspect.length,
        totalProfileCount: profiles.length,
        preferredProfileKey: preferredProfileKey ?? null,
      });

      const tInspect = performance.now();
      try {
        const inspectionResults = await Promise.all(
          profilesToInspect.map(async (profile) => ({
            profile,
            result: await loadProfileSettings(profile),
          }))
        );
        const cacheHitCount = inspectionResults.filter(({ result }) => result.cacheHit).length;
        const candidates = inspectionResults.map(({ profile, result }) => ({
          profile,
          dto: result.dto,
          error: result.error,
        }));
        perf.slow('MappingProfile', 'profile settings inspection', tInspect, cacheHitCount === profiles.length ? 40 : 180, {
          profiles: profilesToInspect.length,
          totalProfiles: profiles.length,
          cacheHitCount,
          projectKey: normalizedProjectKey,
          modelVersion,
        });

        if (!isActive) {
          traceMappingFlow('project profile match inspection stale ignored', {
            projectKey,
            normalizedProjectKey,
            modelVersion,
            profileSignature,
          });
          return;
        }

        traceMappingFlow('project profile match inspection complete', {
          projectKey,
          normalizedProjectKey,
          modelVersion,
          profileCount: profilesToInspect.length,
          totalProfileCount: profiles.length,
          preferredProfileKey: preferredProfileKey ?? null,
          cacheHitCount,
          errorCount: candidates.filter((candidate) => candidate.error).length,
          candidateSummaries: candidates.map((candidate) => ({
            profileKey: buildProfileKey(candidate.profile),
            hasDto: Boolean(candidate.dto),
            error: candidate.error,
          })),
        });
        startTransition(() => {
          setProfileInspection({
            projectKey: normalizedProjectKey,
            modelVersion,
            profileSignature,
            isLoading: false,
            candidates,
          });
        });
      } catch (error) {
        warnMappingFlow('project profile match inspection failed', {
          projectKey,
          normalizedProjectKey,
          modelVersion,
          profileCount: profilesToInspect.length,
          totalProfileCount: profiles.length,
          preferredProfileKey: preferredProfileKey ?? null,
          error: error instanceof Error ? error.message : 'Profile inspection failed.',
        });
        if (isActive) {
          startTransition(() => {
            setProfileInspection({
              projectKey: normalizedProjectKey,
              modelVersion,
              profileSignature,
              isLoading: false,
              candidates: [],
            });
          });
        }
      } finally {
        globalThis.clearTimeout(stallTimer);
      }
    };

    void inspectProfiles();

    return () => {
      isActive = false;
    };
  }, [
    enabled,
    modelVersion,
    normalizedProjectKey,
    preferredProfileKey,
    profileSignature,
    profiles.length,
    profilesToInspect,
    projectKey,
  ]);

  return profileInspection;
}
